import { useState, useRef } from "react";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  ArrowRight,
  Eye,
  Check,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TARGET_COLUMNS = [
  { key: "full_name", label: "Full Name", required: true },
  { key: "company", label: "Company" },
  { key: "category", label: "Category" },
  { key: "power_team", label: "Power Team" },
  { key: "tag", label: "Tag (Visitor/Guest)" },
  { key: "invited_by_member", label: "Invited By" },
  { key: "mobile", label: "Mobile" },
  { key: "email", label: "Email" },
  { key: "first_visit_date", label: "First Visit Date" },
  { key: "joining_interest", label: "Interest Level (1/2/3)" },
  { key: "status", label: "Status" },
  { key: "notes", label: "Notes" }
];

export const ImportPage = () => {
  const { chapter } = useAuth();
  const [activeTab, setActiveTab] = useState("upload");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importMode, setImportMode] = useState("skip_duplicates");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [loadingReview, setLoadingReview] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploadedFile(file);
    
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/import/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setParsedData(data);
        
        // Auto-map columns
        const autoMapping = {};
        TARGET_COLUMNS.forEach(target => {
          const match = data.columns.find(col => 
            col.toLowerCase().replace(/[_\s]/g, '') === target.key.toLowerCase().replace(/[_\s]/g, '') ||
            col.toLowerCase().includes(target.key.toLowerCase()) ||
            target.key.toLowerCase().includes(col.toLowerCase().replace(/[_\s]/g, ''))
          );
          if (match) autoMapping[target.key] = match;
        });
        setMapping(autoMapping);
        
        toast.success(`Uploaded ${data.total_rows} rows`);
        setActiveTab("mapping");
      } else {
        toast.error("Failed to parse CSV");
      }
    } catch (error) {
      toast.error("Error uploading file");
    }
  };

  const handleImport = async () => {
    if (!parsedData) return;

    setImporting(true);
    try {
      const response = await fetch(`${API}/import/process`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          rows: parsedData.all_rows,
          mapping,
          mode: importMode
        })
      });

      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        toast.success(`Import complete: ${result.created} created, ${result.skipped} skipped`);
        setActiveTab("results");
      } else {
        toast.error("Import failed");
      }
    } catch (error) {
      toast.error("Error during import");
    } finally {
      setImporting(false);
    }
  };

  const fetchReviewQueue = async () => {
    setLoadingReview(true);
    try {
      const response = await fetch(`${API}/import/review-queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setReviewQueue(await response.json());
      }
    } catch (error) {
      console.error("Error fetching review queue:", error);
    } finally {
      setLoadingReview(false);
    }
  };

  const handleApprove = async (item) => {
    const name = prompt("Enter the full name for this record:", item.data.full_name || item.data.company_or_category || "");
    if (!name) return;

    try {
      const response = await fetch(`${API}/import/review/${item.queue_id}/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: name,
          company: item.data.company_or_category,
          tag: item.data.tag || "Visitor",
          notes: item.data.notes
        })
      });

      if (response.ok) {
        toast.success("Record approved and imported");
        fetchReviewQueue();
      }
    } catch (error) {
      toast.error("Error approving record");
    }
  };

  const handleReject = async (queueId) => {
    try {
      const response = await fetch(`${API}/import/review/${queueId}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Record rejected");
        fetchReviewQueue();
      }
    } catch (error) {
      toast.error("Error rejecting record");
    }
  };

  const downloadTemplate = () => {
    const headers = TARGET_COLUMNS.map(c => c.key).join(',');
    const exampleRow = "John Doe,Acme Corp,Financial Advisor,Business Services,Visitor,Jane Smith,+91 9876543210,john@example.com,2024-01-15,1,Active,First time visitor";
    const csv = `${headers}\n${exampleRow}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bni_visitors_bulk_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetImport = () => {
    setUploadedFile(null);
    setParsedData(null);
    setMapping({});
    setImportResult(null);
    setActiveTab("upload");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="import-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Import Data</h1>
            <p className="text-muted-foreground mt-1">Bulk import visitors from CSV files</p>
          </div>
          
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="mr-2 h-4 w-4" />
            Download Template
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="upload">1. Upload</TabsTrigger>
            <TabsTrigger value="mapping" disabled={!parsedData}>2. Map Fields</TabsTrigger>
            <TabsTrigger value="preview" disabled={!parsedData}>3. Preview</TabsTrigger>
            <TabsTrigger value="results" disabled={!importResult}>4. Results</TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload" className="mt-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div 
                  className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center hover:border-primary transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileUpload}
                    data-testid="file-input"
                  />
                  <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-lg font-medium text-slate-700">
                    {uploadedFile ? uploadedFile.name : "Click to upload CSV file"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    or drag and drop your file here
                  </p>
                </div>

                {parsedData && (
                  <div className="mt-6 p-4 bg-green-50 rounded-lg flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">{parsedData.total_rows} rows detected</p>
                      <p className="text-sm text-green-600">{parsedData.columns.length} columns found</p>
                    </div>
                    <Button 
                      className="ml-auto" 
                      style={{ backgroundColor: primaryColor }}
                      onClick={() => setActiveTab("mapping")}
                    >
                      Continue <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Review Queue Section */}
            <Card className="border-0 shadow-sm mt-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-display">Review Queue</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchReviewQueue}>
                  <Eye className="mr-2 h-4 w-4" />
                  Load Queue
                </Button>
              </CardHeader>
              <CardContent>
                {loadingReview ? (
                  <div className="py-8 text-center">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                ) : reviewQueue.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No items in review queue. Click "Load Queue" to check for pending reviews.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {reviewQueue.map((item) => (
                      <div key={item.queue_id} className="p-4 border rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium">{item.data.full_name || item.data.company_or_category || "Unknown"}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.import_metadata?.open_classification_context || item.data.power_team || "No context"}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleApprove(item)}>
                            <Check className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReject(item.queue_id)}>
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mapping Tab */}
          <TabsContent value="mapping" className="mt-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-display">Map CSV Columns to Fields</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {TARGET_COLUMNS.map((target) => (
                    <div key={target.key} className="flex items-center gap-4">
                      <div className="w-40">
                        <Label className="flex items-center gap-1">
                          {target.label}
                          {target.required && <span className="text-red-500">*</span>}
                        </Label>
                      </div>
                      <Select 
                        value={mapping[target.key] || ""} 
                        onValueChange={(v) => setMapping({ ...mapping, [target.key]: v })}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">-- Not mapped --</SelectItem>
                          {parsedData?.columns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <Label className="mb-2 block">Import Mode</Label>
                  <Select value={importMode} onValueChange={setImportMode}>
                    <SelectTrigger className="w-full max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="create_only">Create only (fail on duplicates)</SelectItem>
                      <SelectItem value="skip_duplicates">Skip duplicates</SelectItem>
                      <SelectItem value="update_existing">Update existing records</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <Button variant="outline" onClick={resetImport}>Reset</Button>
                  <Button 
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => setActiveTab("preview")}
                    disabled={!mapping.full_name}
                  >
                    Preview Import <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="mt-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-display">Preview (First 10 Rows)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        {TARGET_COLUMNS.filter(t => mapping[t.key]).map(t => (
                          <TableHead key={t.key}>{t.label}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedData?.preview.map((row) => (
                        <TableRow key={row.row_number}>
                          <TableCell>{row.row_number}</TableCell>
                          {TARGET_COLUMNS.filter(t => mapping[t.key]).map(t => (
                            <TableCell key={t.key}>
                              {row.data[mapping[t.key]] || "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setActiveTab("mapping")}>Back</Button>
                  <Button 
                    style={{ backgroundColor: primaryColor }}
                    onClick={handleImport}
                    disabled={importing}
                    data-testid="import-btn"
                  >
                    {importing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Importing...
                      </>
                    ) : (
                      <>Import {parsedData?.total_rows} Rows</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="mt-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-display">Import Results</CardTitle>
              </CardHeader>
              <CardContent>
                {importResult && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-green-700">{importResult.created}</p>
                        <p className="text-sm text-green-600">Created</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <ArrowRight className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-blue-700">{importResult.updated}</p>
                        <p className="text-sm text-blue-600">Updated</p>
                      </div>
                      <div className="p-4 bg-yellow-50 rounded-lg text-center">
                        <AlertTriangle className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-yellow-700">{importResult.skipped}</p>
                        <p className="text-sm text-yellow-600">Skipped</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <Eye className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-purple-700">{importResult.review_queue}</p>
                        <p className="text-sm text-purple-600">Review Queue</p>
                      </div>
                    </div>

                    {importResult.errors?.length > 0 && (
                      <div className="p-4 bg-red-50 rounded-lg">
                        <h3 className="font-medium text-red-800 mb-2">Errors ({importResult.errors.length})</h3>
                        <ul className="text-sm text-red-600 space-y-1">
                          {importResult.errors.slice(0, 10).map((err, i) => (
                            <li key={i}>Row {err.row}: {err.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={resetImport}>Import More</Button>
                      <Button 
                        style={{ backgroundColor: primaryColor }}
                        onClick={() => window.location.href = "/visitors"}
                      >
                        View Visitors
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ImportPage;
